import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Textarea } from '@/components/textarea';
import {
  useAddComment,
  useCardComments,
  useCards,
  useCreateCard,
  useDeleteCard,
  useDeleteCardAttachment,
  useDeleteComment,
  useDeleteProject,
  useProject,
  useUpdateCard,
  useUpdateProject,
  useUploadCardAttachment,
} from '@/lib/projects';
import {
  PROJECT_TYPE_LABEL,
  PROJECT_STATUS_LABEL,
  PROJECT_STATUS_TONE,
  type ProjectStatus,
} from '@/lib/project-types';
import { formatDate, formatDateTime, formatStatus } from '@/lib/format';
import { Select } from '@/components/select';
import { ProjectStatusSelect } from '@/components/project-status-select';
import { Menu, type MenuItem } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useNavigate } from 'react-router-dom';
import {
  Pencil,
  Trash2,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Download,
  X as XIcon,
  Upload,
  MessageSquare,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react';
import type { CardAttachmentData } from '@/types/api';
import type { CardData } from '@/types/api';
import { cn } from '@/lib/cn';
import { useFormError } from '@/lib/form-error';
import { useMe } from '@/lib/auth';
import { isAdminSurface } from '@/lib/surface';

// removed unused dialog import — controlled via inline JSX below

const COLUMNS = [
  { key: 'todo', label: 'To do' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
] as const;

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = id ? Number(id) : undefined;
  const project = useProject(projectId);
  const cards = useCards(projectId);
  const updateCard = useUpdateCard(projectId ?? 0);
  const updateProject = useUpdateProject(projectId ?? 0);
  const deleteProject = useDeleteProject();
  const [creating, setCreating] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);

  // Always look the viewed card up from the latest cards data so the detail
  // dialog re-renders whenever an attachment is uploaded, the status changes,
  // a comment is added, etc. Holding a snapshot in state would leave it stale.
  const viewing = viewingId != null ? (cards.data ?? []).find((c) => c.id === viewingId) ?? null : null;

  const grouped = useMemo(() => {
    const map: Record<string, CardData[]> = { todo: [], in_progress: [], review: [], done: [] };
    (cards.data ?? []).forEach((c) => {
      if (map[c.status]) map[c.status].push(c);
    });
    return map;
  }, [cards.data]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  function onDragStart(e: DragStartEvent) {
    setActiveId(Number(e.active.id));
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const cardId = Number(e.active.id);
    const newStatus = String(e.over?.id ?? '');
    if (!newStatus || !COLUMNS.some((c) => c.key === newStatus)) return;
    const card = (cards.data ?? []).find((c) => c.id === cardId);
    if (!card || card.status === newStatus) return;
    updateCard.mutate({ id: cardId, patch: { status: newStatus } });
  }

  const activeCard = activeId != null ? (cards.data ?? []).find((c) => c.id === activeId) ?? null : null;

  if (!project.data) {
    return <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>;
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <ProjectHeader
        project={project.data}
        onEdit={() => setEditing(true)}
        onStatusChange={(status) => updateProject.mutate({ status })}
        onDelete={() => setConfirmDelete(true)}
        toggling={updateProject.isPending}
        showActions={isAdminSurface}
      />

      <ProgressSummary cards={cards.data ?? []} />

      <DndContext
        sensors={sensors}
        autoScroll={false}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="grid min-h-0 flex-1 grid-cols-4 grid-rows-[minmax(0,1fr)] gap-3 overflow-x-auto">
          {COLUMNS.map((col) => (
            <Column
              key={col.key}
              columnKey={col.key}
              label={col.label}
              cards={grouped[col.key] ?? []}
              onAdd={() => setCreating(col.key)}
              onOpen={(card) => setViewingId(card.id)}
            />
          ))}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeCard ? <CardPreview card={activeCard} /> : null}
        </DragOverlay>
      </DndContext>

      <CreateCardDialog
        projectId={projectId ?? 0}
        status={creating}
        onClose={() => setCreating(null)}
      />

      {editing ? (
        <EditProjectDialog
          project={project.data}
          projectId={projectId ?? 0}
          onClose={() => setEditing(false)}
        />
      ) : null}

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Delete ${project.data.name}?`}
      >
        <p className="text-sm text-[var(--color-foreground-muted)]">
          The project and all its cards will be removed.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleteProject.isPending}
            onClick={async () => {
              if (!projectId) return;
              await deleteProject.mutateAsync(projectId);
              navigate(`/clients/${project.data.client_id}`);
            }}
          >
            {deleteProject.isPending ? 'Deleting…' : 'Delete project'}
          </Button>
        </div>
      </Dialog>

      <CardDetailDialog card={viewing} onClose={() => setViewingId(null)} projectId={projectId ?? 0} />
    </div>
  );
}

function Column({
  columnKey,
  label,
  cards,
  onAdd,
  onOpen,
}: {
  columnKey: string;
  label: string;
  cards: CardData[];
  onAdd: () => void;
  onOpen: (card: CardData) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnKey });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-0 flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/30 p-2 transition-colors',
        isOver && 'border-[var(--color-ring)] bg-[var(--color-surface-muted)]',
      )}
    >
      <div className="mb-2 flex items-center justify-between px-1 text-xs font-medium text-[var(--color-foreground-muted)]">
        <span className="inline-flex items-center gap-1.5">
          <span>{label}</span>
          {cards.length > 0 ? (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-surface)] px-1.5 text-[10px] tabular-nums">
              {cards.length}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          onClick={onAdd}
          className="rounded p-1 hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground)]"
          aria-label={`Add card to ${label}`}
        >
          <Plus className="size-3.5" />
        </button>
      </div>
      {cards.length === 0 ? (
        <button
          type="button"
          onClick={onAdd}
          className="flex min-h-[6rem] flex-1 items-center justify-center rounded-md border border-dashed border-[var(--color-border)] text-xs text-[var(--color-foreground-subtle)] transition-colors hover:border-[var(--color-foreground-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-foreground-muted)]"
        >
          <Plus className="mr-1 size-3.5" /> Add card
        </button>
      ) : (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-0.5">
          {cards.map((c) => (
            <DraggableCard key={c.id} card={c} onOpen={() => onOpen(c)} />
          ))}
        </div>
      )}
    </div>
  );
}

function DraggableCard({ card, onOpen }: { card: CardData; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onDoubleClick={onOpen}
      className={cn(
        'group cursor-grab rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-sm transition-shadow active:cursor-grabbing',
        // Keep the card in place (reserving its space) but dim it while its
        // overlay copy follows the cursor — so sibling cards never jump.
        isDragging && 'opacity-40',
      )}
    >
      <CardFace card={card} onOpen={onOpen} />
    </div>
  );
}

/** The visual content of a card. Shared by the board card and the drag overlay. */
function CardFace({ card, onOpen }: { card: CardData; onOpen?: () => void }) {
  return (
    <>
      <div className="text-sm font-medium">{card.title}</div>
      {card.body ? (
        <div className="mt-1 line-clamp-2 text-xs text-[var(--color-foreground-muted)]">{card.body}</div>
      ) : null}
      {card.due_date ? (
        <div className="mt-2">
          <DueDateBadge dueDate={card.due_date} status={card.status} />
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between text-xs text-[var(--color-foreground-subtle)]">
        <span>{card.assigned_to_name ?? ''}</span>
        {onOpen ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className="flex items-center gap-1 hover:text-[var(--color-foreground)]"
            aria-label="Open card"
          >
            <CardCounts card={card} fallback="Open" />
          </button>
        ) : (
          <CardCounts card={card} fallback="" />
        )}
      </div>
    </>
  );
}

function CardCounts({ card, fallback }: { card: CardData; fallback: string }) {
  if (card.comments_count === 0 && card.attachments_count === 0) {
    return <>{fallback}</>;
  }
  return (
    <span className="flex items-center gap-2">
      {card.attachments_count > 0 ? (
        <span className="flex items-center gap-1">
          <Paperclip className="size-3" />
          {card.attachments_count}
        </span>
      ) : null}
      {card.comments_count > 0 ? (
        <span className="flex items-center gap-1">
          <MessageSquare className="size-3" />
          {card.comments_count}
        </span>
      ) : null}
    </span>
  );
}

/** The "picked up" card rendered inside DragOverlay — follows the cursor. */
function CardPreview({ card }: { card: CardData }) {
  return (
    <div className="w-full cursor-grabbing rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-lg">
      <CardFace card={card} />
    </div>
  );
}

function CreateCardDialog({
  projectId,
  status,
  onClose,
}: {
  projectId: number;
  status: string | null;
  onClose: () => void;
}) {
  const create = useCreateCard(projectId);
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { error, wrap } = useFormError();

  function addFiles(list: FileList | File[]) {
    setFiles((curr) => [...curr, ...Array.from(list)]);
  }

  function removeFile(idx: number) {
    setFiles((curr) => curr.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!title || !status) return;
    const ok = await wrap(async () => {
      const card = await create.mutateAsync({
        title,
        body: body || undefined,
        status,
        due_date: dueDate || undefined,
      });
      if (files.length > 0) {
        await Promise.all(
          files.map(async (file) => {
            const fd = new FormData();
            fd.append('file', file);
            await api.post(`/v1/cards/${card.id}/attachments`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }),
        );
        qc.invalidateQueries({ queryKey: ['cards', projectId] });
      }
    });
    if (ok) {
      setTitle('');
      setBody('');
      setDueDate('');
      setFiles([]);
      onClose();
    }
  }

  return (
    <Dialog open={status != null} onClose={onClose} title="New card">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <Input
          autoFocus
          required
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea
          placeholder="Details (optional)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <label className="block space-y-1">
          <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-foreground-muted)]">
            <CalendarClock className="size-3.5" /> Due date (optional)
          </span>
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-muted)]">
              <Paperclip className="size-3.5" /> Attachments
              {files.length > 0 ? (
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-surface-muted)] px-1.5 text-[10px] tabular-nums">
                  {files.length}
                </span>
              ) : null}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="size-3.5" /> Add files
            </Button>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
            }}
            className={
              files.length === 0
                ? `flex items-center justify-center rounded-md border border-dashed py-4 text-xs transition-colors ${
                    dragging
                      ? 'border-[var(--color-primary)] bg-[var(--color-surface-muted)] text-[var(--color-foreground)]'
                      : 'border-[var(--color-border)] text-[var(--color-foreground-subtle)]'
                  }`
                : `space-y-1.5 rounded-md border border-dashed p-2 transition-colors ${
                    dragging ? 'border-[var(--color-primary)] bg-[var(--color-surface-muted)]' : 'border-transparent'
                  }`
            }
          >
            {files.length === 0 ? (
              <span>Drop files here or click Add files</span>
            ) : (
              files.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  className="flex items-center gap-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs"
                >
                  <Paperclip className="size-3 shrink-0 text-[var(--color-foreground-muted)]" />
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="shrink-0 text-[var(--color-foreground-muted)]">
                    {formatBytes(f.size)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="rounded p-0.5 text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)]"
                    aria-label={`Remove ${f.name}`}
                  >
                    <XIcon className="size-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add card'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CardDetailDialog({
  card,
  onClose,
  projectId,
}: {
  card: CardData | null;
  onClose: () => void;
  projectId: number;
}) {
  const comments = useCardComments(card?.id);
  const addComment = useAddComment(card?.id ?? 0);
  const updateCard = useUpdateCard(projectId);
  const deleteCard = useDeleteCard(projectId);
  const deleteComment = useDeleteComment(card?.id ?? 0);
  const { data: me } = useMe();
  const confirm = useConfirm();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const { error: editError, wrap: wrapEdit } = useFormError();

  async function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!body) return;
    await addComment.mutateAsync(body);
    setBody('');
  }

  function startEditing() {
    if (!card) return;
    setEditTitle(card.title);
    setEditBody(card.body ?? '');
    setEditDueDate(card.due_date ?? '');
    setEditing(true);
  }

  async function saveEdit() {
    if (!card) return;
    const ok = await wrapEdit(() =>
      updateCard.mutateAsync({
        id: card.id,
        patch: { title: editTitle, body: editBody, due_date: editDueDate || null },
      }),
    );
    if (ok) setEditing(false);
  }

  if (!card) return null;

  return (
    <Dialog open onClose={onClose} title={editing ? `Edit ${card.title}` : card.title} size="lg">
      <div className="space-y-4">
        {editing ? (
          <div className="space-y-2">
            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            <Textarea
              placeholder="Details"
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
            />
            <label className="block space-y-1">
              <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-foreground-muted)]">
                <CalendarClock className="size-3.5" /> Due date
              </span>
              <Input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
              />
            </label>
            {editError ? (
              <p className="text-sm text-[var(--color-danger)]">{editError}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={saveEdit} disabled={updateCard.isPending}>
                {updateCard.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                {card.body ? (
                  <p className="whitespace-pre-wrap text-sm">{card.body}</p>
                ) : (
                  <p className="text-sm italic text-[var(--color-foreground-subtle)]">
                    No description.
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" variant="ghost" onClick={startEditing}>
                  Edit
                </Button>
                {/* Deleting a card is admin-only on the server, and the board is
                    shared with the client's own portal users. */}
                {isAdminSurface ? (
                  <Menu
                    items={[
                      {
                        key: 'delete',
                        label: 'Delete card',
                        icon: Trash2,
                        danger: true,
                        onSelect: async () => {
                          const ok = await confirm({
                            title: `Delete ${card.title}?`,
                            body: 'The card goes for good, with its comments and attachments. There is no trash for cards.',
                            confirmLabel: 'Delete card',
                            tone: 'danger',
                          });
                          if (ok) {
                            await deleteCard.mutateAsync(card.id);
                            toast.success('Card deleted');
                            onClose();
                          }
                        },
                      },
                    ]}
                  />
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {COLUMNS.map((col) => (
                <Button
                  key={col.key}
                  size="sm"
                  variant={col.key === card.status ? 'primary' : 'secondary'}
                  onClick={() => updateCard.mutate({ id: card.id, patch: { status: col.key } })}
                >
                  {col.label}
                </Button>
              ))}
            </div>
          </>
        )}

        <CardAttachments card={card} />

        <div className="space-y-2 border-t border-[var(--color-border)] pt-4">
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-muted)]">
            <MessageSquare className="size-3.5" /> Comments
          </h3>
          {comments.data?.length === 0 ? (
            <p className="text-sm italic text-[var(--color-foreground-subtle)]">No comments yet.</p>
          ) : (
            <ul className="space-y-2">
              {comments.data?.map((c) => (
                <li key={c.id} className="group/comment text-sm">
                  <div className="flex items-center gap-1 font-medium">
                    <span>
                      {c.author_name}{' '}
                      <span className="text-xs font-normal text-[var(--color-foreground-subtle)]">
                        · {formatDateTime(c.created_at)}
                      </span>
                    </span>
                    {isAdminSurface || (me != null && c.user_id === me.id) ? (
                      <button
                        type="button"
                        aria-label="Delete comment"
                        className="rounded p-0.5 text-[var(--color-foreground-subtle)] opacity-0 transition-opacity hover:text-[var(--color-danger)] focus:opacity-100 group-hover/comment:opacity-100"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Delete this comment?',
                            body: 'It is removed from the card for everyone.',
                            confirmLabel: 'Delete comment',
                            tone: 'danger',
                          });
                          if (ok) {
                            await deleteComment.mutateAsync(c.id);
                            toast.success('Comment deleted');
                          }
                        }}
                      >
                        <Trash2 className="size-3" />
                      </button>
                    ) : null}
                  </div>
                  <div className="whitespace-pre-wrap">{c.body}</div>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={handleAdd} className="space-y-2 pt-2">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Add a comment…"
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={addComment.isPending || !body.trim()}>
                {addComment.isPending ? 'Posting…' : 'Comment'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}

function ProgressSummary({ cards }: { cards: CardData[] }) {
  if (cards.length === 0) return null;

  const total = cards.length;
  const done = cards.filter((c) => c.status === 'done').length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = cards.filter(
    (c) => c.status !== 'done' && c.due_date != null && c.due_date < today,
  ).length;
  const nextDue = cards
    .filter((c) => c.status !== 'done' && c.due_date != null && c.due_date >= today)
    .sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1))[0];

  return (
    <div className="rounded-xl border border-border/70 bg-card p-4 shadow-xs">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="font-medium">
          {done} of {total} tasks complete
        </span>
        <span className="flex items-center gap-3 text-xs text-[var(--color-foreground-muted)]">
          {overdue > 0 ? (
            <span className="inline-flex items-center gap-1 text-[var(--color-danger)]">
              <AlertTriangle className="size-3.5" /> {overdue} overdue
            </span>
          ) : null}
          {nextDue ? (
            <span className="inline-flex items-center gap-1">
              <CalendarClock className="size-3.5" /> Next due {formatDate(nextDue.due_date!)}
            </span>
          ) : null}
          <span className="font-medium text-[var(--color-foreground)]">{pct}%</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-muted)]">
        <div className="h-full rounded-full bg-[var(--color-primary)] transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ProjectHeader({
  project,
  onEdit,
  onStatusChange,
  onDelete,
  toggling,
  showActions,
}: {
  project: { name: string; client_name: string; type: string; status: string; description: string | null };
  onEdit: () => void;
  onStatusChange: (status: ProjectStatus) => void;
  onDelete: () => void;
  toggling: boolean;
  showActions: boolean;
}) {
  const status = (project.status as ProjectStatus) ?? 'active';
  const menuItems: MenuItem[] = [
    {
      key: 'delete',
      label: 'Delete project',
      icon: Trash2,
      onSelect: onDelete,
      danger: true,
    },
  ];

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 space-y-1.5">
        <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
          {project.client_name}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{project.name}</h1>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone="neutral">{PROJECT_TYPE_LABEL[project.type] ?? project.type}</Badge>
          {showActions ? (
            <ProjectStatusSelect value={status} disabled={toggling} onChange={onStatusChange} />
          ) : (
            <Badge tone={PROJECT_STATUS_TONE[status] ?? 'neutral'}>
              {PROJECT_STATUS_LABEL[status] ?? formatStatus(project.status)}
            </Badge>
          )}
        </div>
        {project.description ? (
          <p className="max-w-2xl pt-1 text-sm text-[var(--color-foreground-muted)]">
            {project.description}
          </p>
        ) : null}
      </div>
      {showActions ? (
        <div className="flex shrink-0 items-center gap-1">
          <Button variant="secondary" onClick={onEdit}>
            <Pencil className="size-3.5" /> Edit
          </Button>
          <Menu items={menuItems} />
        </div>
      ) : null}
    </div>
  );
}

function EditProjectDialog({
  project,
  projectId,
  onClose,
}: {
  project: { name: string; description: string | null; type: string };
  projectId: number;
  onClose: () => void;
}) {
  const update = useUpdateProject(projectId);
  const { error, wrap } = useFormError();
  const [name, setName] = useState(project.name);
  const [type, setType] = useState(project.type);
  const [description, setDescription] = useState(project.description ?? '');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      update.mutateAsync({ name, type, description: description || null }),
    );
    if (ok) onClose();
  }

  return (
    <Dialog open onClose={onClose} title="Edit project">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <Input
          required
          placeholder="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          {Object.entries(PROJECT_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Textarea
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}


function CardAttachments({ card }: { card: CardData }) {
  const upload = useUploadCardAttachment(card.id);
  const remove = useDeleteCardAttachment(card.id);
  const { error, wrap, clear } = useFormError();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  async function handleFiles(files: FileList | File[]) {
    clear();
    for (const file of Array.from(files)) {
      await wrap(() => upload.mutateAsync(file));
    }
  }

  return (
    <div className="space-y-2 border-t border-[var(--color-border)] pt-4">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-muted)]">
          <Paperclip className="size-3.5" /> Attachments
          {card.attachments.length > 0 ? (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-surface-muted)] px-1.5 text-[10px] tabular-nums">
              {card.attachments.length}
            </span>
          ) : null}
        </h3>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          <Upload className="size-3.5" /> {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
        }}
        className={
          card.attachments.length === 0
            ? `flex items-center justify-center rounded-md border border-dashed py-6 text-xs transition-colors ${
                dragging
                  ? "border-[var(--color-primary)] bg-[var(--color-surface-muted)] text-[var(--color-foreground)]"
                  : "border-[var(--color-border)] text-[var(--color-foreground-subtle)]"
              }`
            : `grid grid-cols-1 gap-2 rounded-md border border-dashed p-2 transition-colors sm:grid-cols-2 ${
                dragging
                  ? "border-[var(--color-primary)] bg-[var(--color-surface-muted)]"
                  : "border-transparent"
              }`
        }
      >
        {card.attachments.length === 0 ? (
          <span>Drop files here or click Upload</span>
        ) : (
          card.attachments.map((a) => (
            <AttachmentRow
              key={a.id}
              attachment={a}
              onDelete={() => remove.mutate(a.id)}
            />
          ))
        )}
      </div>
      {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
    </div>
  );
}

function AttachmentRow({
  attachment,
  onDelete,
}: {
  attachment: CardAttachmentData;
  onDelete: () => void;
}) {
  const Icon = attachment.is_image ? ImageIcon : FileText;
  return (
    <div className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-sm">
      {attachment.is_image ? (
        <img
          src={attachment.url}
          alt={attachment.file_name}
          className="size-10 shrink-0 rounded object-cover"
        />
      ) : (
        <div className="flex size-10 shrink-0 items-center justify-center rounded bg-[var(--color-surface-muted)]">
          <Icon className="size-4 text-[var(--color-foreground-muted)]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{attachment.file_name}</div>
        <div className="text-xs text-[var(--color-foreground-muted)]">
          {formatBytes(attachment.file_size)}
        </div>
      </div>
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        download={attachment.file_name}
        className="rounded p-1 text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
        aria-label={`Download ${attachment.file_name}`}
      >
        <Download className="size-4" />
      </a>
      <button
        type="button"
        onClick={onDelete}
        className="rounded p-1 text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)]"
        aria-label={`Delete ${attachment.file_name}`}
      >
        <XIcon className="size-4" />
      </button>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}


function DueDateBadge({ dueDate, status }: { dueDate: string; status: string }) {
  const done = status === "done";
  const date = new Date(dueDate + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayMs = 24 * 60 * 60 * 1000;
  const daysUntil = Math.round((date.getTime() - today.getTime()) / dayMs);

  const label = formatDueLabel(daysUntil, date);
  const tone: "success" | "warning" | "danger" | "neutral" =
    done ? "neutral" : daysUntil < 0 ? "danger" : daysUntil <= 3 ? "warning" : "neutral";
  const Icon = daysUntil < 0 && !done ? AlertTriangle : CalendarClock;

  return (
    <Badge tone={tone}>
      <Icon className="mr-1 size-3" />
      {label}
    </Badge>
  );
}

function formatDueLabel(daysUntil: number, date: Date): string {
  if (daysUntil === 0) return "Due today";
  if (daysUntil === 1) return "Due tomorrow";
  if (daysUntil === -1) return "1 day overdue";
  if (daysUntil < 0) return `${Math.abs(daysUntil)} days overdue`;
  if (daysUntil < 7) return `Due in ${daysUntil} days`;
  return `Due ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}
